import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageSquare, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { chatApi, type ListSessionsParams } from '@/api/chat';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { CHAT_KINDS, CHAT_SESSION_STATUSES, type ChatKind, type ChatSessionStatus } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SessionList } from './SessionList';
import { MessageTimeline } from './MessageTimeline';
import { formatDate } from '@/lib/utils';

interface BrowseProps {
  onResume: (sessionId: string) => void;
}

export function Browse({ onResume }: BrowseProps): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId, currentBot } = useWorkspace();
  const [kind, setKind] = React.useState<ChatKind | 'all'>('all');
  const [status, setStatus] = React.useState<ChatSessionStatus | 'all'>('all');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const params: ListSessionsParams = React.useMemo(
    () => ({
      ...(kind !== 'all' ? { kind } : {}),
      ...(status !== 'all' ? { status } : {}),
      limit: 50,
    }),
    [kind, status],
  );

  const queryClient = useQueryClient();

  const sessionsQuery = useQuery({
    queryKey: ['chat', 'sessions', currentBotId, kind, status],
    queryFn: () => chatApi.listOwnSessions(params),
    enabled: !!currentBotId,
  });

  const detailQuery = useQuery({
    queryKey: ['chat', 'session', selectedId],
    queryFn: () => chatApi.getSession(selectedId!, { limit: 200, ascending: true }),
    enabled: !!selectedId,
  });

  const deleteSession = async (sessionId: string): Promise<void> => {
    if (!window.confirm(t('chat.browse.deleteConfirm'))) return;
    try {
      await chatApi.deleteSession(sessionId);
      toast.success(t('chat.browse.deletedToast'));
      setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('common.selectBotInTopbar')}
      </div>
    );
  }

  const sessions = sessionsQuery.data?.items ?? [];
  const botLabel = currentBot?.name ?? currentBotId;

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card className="flex h-[70vh] flex-col">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">{t('chat.browse.mySessions')}</CardTitle>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => void sessionsQuery.refetch()}
              disabled={sessionsQuery.isFetching}
              title={t('chat.browse.refreshAria')}
            >
              {sessionsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={kind} onValueChange={(v) => setKind(v as ChatKind | 'all')}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('chat.browse.allKinds')}</SelectItem>
                {CHAT_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as ChatSessionStatus | 'all')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('chat.browse.allStatuses')}</SelectItem>
                {CHAT_SESSION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            <Trans
              i18nKey={sessions.length > 1 ? 'chat.browse.botInfoPlural' : 'chat.browse.botInfo'}
              values={{ bot: botLabel, count: sessions.length }}
              components={{ mono: <span className="font-mono" /> }}
            />
          </p>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0">
          {sessionsQuery.isLoading ? (
            <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
            </p>
          ) : sessionsQuery.error ? (
            <p className="p-6 text-sm text-destructive">{unwrapError(sessionsQuery.error)}</p>
          ) : (
            <SessionList
              sessions={sessions}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </CardContent>
      </Card>

      <Card className="flex h-[70vh] flex-col">
        {!selectedId ? (
          <CardContent className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            {t('chat.browse.selectSession')}
          </CardContent>
        ) : detailQuery.isLoading ? (
          <CardContent className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </CardContent>
        ) : detailQuery.error ? (
          <CardContent className="flex flex-1 items-center justify-center text-sm text-destructive">
            {unwrapError(detailQuery.error)}
          </CardContent>
        ) : detailQuery.data ? (
          <>
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">
                    {detailQuery.data.session.title ?? t('chat.browse.untitled')}
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px]">
                    {detailQuery.data.session.kind}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px]">
                    {detailQuery.data.session.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  {detailQuery.data.session.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => onResume(detailQuery.data!.session.sessionId)}
                    >
                      <MessageSquare className="h-3 w-3" />
                      {t('chat.browse.resume')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-destructive hover:text-destructive"
                    onClick={() => deleteSession(detailQuery.data!.session.sessionId)}
                  >
                    <Trash2 className="h-3 w-3" />
                    {t('chat.browse.delete')}
                  </Button>
                </div>
              </div>
              <p className="font-mono text-[10px] text-muted-foreground">
                {detailQuery.data.session.sessionId} ·{' '}
                {formatDate(detailQuery.data.session.createdAt)} ·{' '}
                {detailQuery.data.session.tokensIn + detailQuery.data.session.tokensOut}{' '}
                {t('chat.browse.tokens')}
              </p>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <MessageTimeline messages={detailQuery.data.messages.items} />
            </CardContent>
          </>
        ) : null}
      </Card>
    </div>
  );
}
